import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { AutoAwesome, CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { Campaign, CampaignDetails } from '../../../../types/Campaign';
import AIService from '../../../../services/AIService';

interface OverviewProps {
  campaign: Campaign;
  onSave: (updatedCampaign: Partial<Campaign>) => Promise<Campaign>;
  onPendingChanges: (changes: Partial<Campaign>) => void;
}

const Overview: React.FC<OverviewProps> = ({ campaign, onSave, onPendingChanges }) => {
  // Track form data changes
  const [formData, setFormData] = useState<Partial<CampaignDetails>>({
    name: '',
    purpose: '',
    focus: '',
    goals: ''
  });

  // Track save status for UI feedback
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  // setSaveStatus will be used for future save status updates
  
  // Load campaign details when the campaign changes
  useEffect(() => {
    if (campaign?.details) {
      setFormData(campaign.details);
    }
  }, [campaign?.details]);

  // Handle form field changes
  const handleFieldChange = (field: keyof CampaignDetails) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    
    // Update local form state for immediate UI feedback
    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));
    
    // Create a properly typed campaign details object with current values
    // and the newly updated field
    const updatedDetails: CampaignDetails = {
      name: field === 'name' ? newValue : (formData.name || ''),
      purpose: field === 'purpose' ? newValue : (formData.purpose || ''),
      focus: field === 'focus' ? newValue : (formData.focus || ''),
      goals: field === 'goals' ? newValue : (formData.goals || '')
    };
    
    // Register the change with the parent component
    onPendingChanges({
      details: updatedDetails
    });
  };
  
  // Save changes on field blur (when focus moves to another element)
  const handleBlur = async () => {
    if (!campaign?.id) return;
    
    // Create a partial campaign object with the current form data
    const partialCampaign: Partial<Campaign> = {
      details: {
        name: formData.name || '',
        purpose: formData.purpose || '',
        focus: formData.focus || '',
        goals: formData.goals || ''
      }
    };
    
    // Show saving indicator
    setSaveStatus('saving');
    
    try {
      // Save the changes
      const updatedCampaign = await onSave(partialCampaign);
      console.log('Campaign updated on blur:', updatedCampaign);
      
      // Show success indicator briefly
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (error) {
      console.error('Error saving campaign on blur:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Initialize the AI suggestion service
  const aiSuggestionService = useMemo(() => new AIService(), []);

  const [aiLoading, setAiLoading] = useState<{ [field: string]: boolean }>({});
  // Store AI suggestions for potential future use
  const [, setAiSuggestions] = useState<{ [field: string]: string }>({});

  const handleAiSuggestion = async (field: string) => {
    setAiLoading(prev => ({ ...prev, [field]: true }));
    setSnackbar({
      open: true,
      message: 'Fetching AI suggestion...',
      severity: 'info'
    });
    try {
      if (!campaign?.id || !campaign?.organization_id) {
        console.error('Missing required campaign data:', { id: campaign?.id, organization_id: campaign?.organization_id });
        setSnackbar({
          open: true,
          message: 'Cannot get AI suggestion: Missing campaign information',
          severity: 'error'
        });
        setAiLoading(prev => ({ ...prev, [field]: false }));
        return;
      }
      
      // Get the current user input for the field
      const userInput = formData[field as keyof CampaignDetails] as string;
      console.log(`Current user input for ${field}:`, userInput);
      
      // Get the suggestion from the API, passing the user's current input
      const suggestion = await aiSuggestionService.getSuggestion(
        campaign.organization_id,
        campaign.id,
        field,
        userInput // Pass the current user input to guide the AI
      );
      
      console.log(`Received AI suggestion for ${field}:`, suggestion);
      
      // IMMEDIATE UI UPDATE: Update form field immediately for visual feedback
      setFormData(prev => ({
        ...prev,
        [field]: suggestion
      }));
      
      // Prepare the partial campaign object with only the updated field
      const partialCampaign: Partial<Campaign> = {
        details: {
          // Ensure we keep the required name field if it exists
          name: campaign.details?.name || "",
          // Add the updated field
          [field]: suggestion
        } as CampaignDetails
      };
      
      console.log('Sending update to API:', partialCampaign);
      
      // Also notify parent of pending changes
      onPendingChanges(partialCampaign);
      
      // Save directly using the onSave prop
      if (onSave) {
        try {
          // This should return the updated campaign from the API
          const updatedCampaign = await onSave(partialCampaign);
          console.log('Campaign updated after AI suggestion:', updatedCampaign);
          
          // Let the user know it worked
          setSnackbar({
            open: true,
            message: 'AI suggestion applied',
            severity: 'success'
          });
          setAiSuggestions(prev => ({ ...prev, [field]: suggestion }));
        } catch (saveError) {
          console.error('Error saving AI suggestion:', saveError);
          setSnackbar({
            open: true,
            message: 'Failed to apply AI suggestion. Changes are visible but not saved.',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error applying AI suggestion:', error);
      setSnackbar({
        open: true,
        message: 'Failed to get AI suggestion',
        severity: 'error'
      });
    } finally {
      setAiLoading(prev => ({ ...prev, [field]: false }));
    }
  };

  return (
    <Box component="form" noValidate sx={{ position: 'relative' }}>
      {/* Save Status Indicator */}
      <Box sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1100,
        bgcolor: 'background.paper',
        p: 1,
        borderRadius: 1,
        boxShadow: 2,
        display: saveStatus === 'idle' ? 'none' : 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        {saveStatus === 'saving' && (
          <>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Saving...</Typography>
          </>
        )}
        {saveStatus === 'saved' && (
          <>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2" color="success.main">All changes saved</Typography>
          </>
        )}
        {saveStatus === 'error' && (
          <>
            <ErrorIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error.main">Error saving changes</Typography>
          </>
        )}
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>Campaign Overview</Typography>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>AI Assistance:</strong> Type your initial ideas or requirements in any field, then click the sparkle icon 
            to get AI-generated suggestions based on your input. The more details you provide, the more tailored the AI suggestions will be.
          </Typography>
        </Alert>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Campaign Name"
              name="name"
              value={formData.name}
              onChange={handleFieldChange('name')}
              onBlur={handleBlur}
              margin="normal"
              placeholder="Give your mentorship campaign a descriptive name"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 0.5 }}>
              A clear, concise name that reflects the purpose of this mentorship program.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Campaign Purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleFieldChange('purpose')}
              onBlur={handleBlur}
              margin="normal"
              multiline
              rows={8}
              minRows={8}
              maxRows={16}
              inputProps={{ style: { fontSize: 18, lineHeight: 1.6 } }}
              placeholder="What is the purpose of this mentorship program?"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Get AI suggestion">
                      <span>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleAiSuggestion('purpose')}
                          disabled={!!aiLoading['purpose']}
                        >
                          {aiLoading['purpose'] ? <CircularProgress size={22} /> : <AutoAwesome />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 0.5 }}>
              The underlying reason for creating this program and how it aligns with your organization's mission and vision.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Campaign Focus"
              name="focus"
              value={formData.focus}
              onChange={handleFieldChange('focus')}
              onBlur={handleBlur}
              margin="normal"
              multiline
              rows={8}
              minRows={8}
              maxRows={16}
              inputProps={{ style: { fontSize: 18, lineHeight: 1.6 } }}
              placeholder="What is the primary focus of this mentorship program?"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Get AI suggestion">
                      <span>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleAiSuggestion('focus')}
                          disabled={!!aiLoading['focus']}
                        >
                          {aiLoading['focus'] ? <CircularProgress size={22} /> : <AutoAwesome />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 0.5 }}>
              The primary area or skill set that will be emphasized in the mentorship relationships.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Campaign Goals"
              name="goals"
              value={formData.goals}
              onChange={handleFieldChange('goals')}
              onBlur={handleBlur}
              margin="normal"
              multiline
              rows={8}
              minRows={8}
              maxRows={16}
              inputProps={{ style: { fontSize: 18, lineHeight: 1.6 } }}
              placeholder="What are the primary goals of this mentorship program?"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Get AI suggestion">
                      <span>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleAiSuggestion('goals')}
                          disabled={!!aiLoading['goals']}
                        >
                          {aiLoading['goals'] ? <CircularProgress size={22} /> : <AutoAwesome />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block', mt: 0.5 }}>
              Specific, measurable outcomes you hope to achieve through this mentorship program. Consider listing 3-5 key goals.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Overview;
